import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { NotificationService } from 'app/modules/shared/services/notification.service';
import { UpgradeService } from 'app/modules/shared/services/upgrade.service';
import { BackendResponseKeys } from 'app/modules/shared/enums/backendResponseKeys.enum';
import { InformationBarType } from 'app/modules/shared/components/information-bar/information-bar.type';

/**
 * Centralized component for managing backend-based notifications
 * Listens to URL parameters and displays appropriate notifications
 */
@Component({
    selector: 'central-notifications',
    template: `
        <!-- This component is invisible but manages all notifications -->
        <div style="display: none;"></div>
    `,
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CentralNotificationsComponent implements OnInit, OnDestroy {
    
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _translocoService: TranslocoService,
        private _notificationService: NotificationService,
        private _upgradeService: UpgradeService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        // Initialize the component
        this.setupNotificationListeners();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sets up event listeners for notifications
     */
    private setupNotificationListeners(): void {
        // Listen for URL parameter changes to display notifications
        this.checkUrlParameters();
    }

    /**
     * Checks URL parameters to display appropriate notifications
     */
    private checkUrlParameters(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const msg = urlParams.get('msg');
        
        if (msg) {
            this.handleBackendResponse(msg);
        }
    }

    /**
     * Handles backend responses and displays appropriate notifications
     * @param responseKey - Backend response key
     */
    private handleBackendResponse(responseKey: string): void {
        switch (responseKey) {
            // ============================================================================
            // SUBSCRIPTION - Subscription notifications
            // ============================================================================
            case BackendResponseKeys.SUBSCRIPTION_UPGRADE_SUCCESS:
                this.showNotification(InformationBarType.Success, 'settings-partner.subscription.messages.upgrade-completed');
                break;
                
            case BackendResponseKeys.SUBSCRIPTION_PAYMENT_METHOD_ADDED:
                this.showNotification(InformationBarType.Success, 'settings-partner.subscription.messages.payment-added-success');
                break;
                
            case BackendResponseKeys.SUBSCRIPTION_PAYMENT_METHOD_ADD_CANCELLED:
                this.showNotification(InformationBarType.Info, 'settings-partner.subscription.messages.payment-add-cancelled');
                break;
                
            case BackendResponseKeys.SUBSCRIPTION_CHECKOUT_SUCCESS:
                this.showNotification(InformationBarType.Success, 'settings-partner.subscription.messages.subscription-success');
                break;
                
            case BackendResponseKeys.SUBSCRIPTION_CHECKOUT_CANCELLED:
                this.showNotification(InformationBarType.Info, 'settings-partner.subscription.messages.subscription-cancelled');
                break;
                
            default:
                console.warn(`Unrecognized backend response key: ${responseKey}`);
                break;
        }

        // Trigger subscription polling for Stripe return flows (before URL cleanup)
        if (responseKey === BackendResponseKeys.SUBSCRIPTION_UPGRADE_SUCCESS ||
            responseKey === BackendResponseKeys.SUBSCRIPTION_CHECKOUT_SUCCESS) {
            this._upgradeService.handleStripeReturn();
        }

        // Clean up URL after processing the notification
        this.cleanupUrl();
    }

    /**
     * Displays a notification with the specified type and translation key
     * @param type - Notification type (InformationBarType enum value)
     * @param translateKey - Translation key to use
     * @param params - Optional parameters for translation
     */
    private showNotification(type: InformationBarType, translateKey: string, params?: any): void {
        const message = this._translocoService.translate(translateKey, params);
        
        switch (type) {
            case InformationBarType.Success:
                this._notificationService.success(message);
                break;
            case InformationBarType.Info:
                this._notificationService.info(message);
                break;
            case InformationBarType.Error:
                this._notificationService.error(message);
                break;
            case InformationBarType.Warning:
                this._notificationService.warning(message);
                break;
        }
    }

    /**
     * Cleans up URL by removing message parameters
     */
    private cleanupUrl(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete('msg');
        url.searchParams.delete('subscriptionId');
        url.searchParams.delete('targetPlan');
        url.searchParams.delete('yearly');
        window.history.replaceState({}, document.title, url.toString());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Utility methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forces change detection
     */
    detectChanges(): void {
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Marks the view for change detection
     */
    markForCheck(): void {
        this._changeDetectorRef.markForCheck();
    }
}
